import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const navItems = [
  { label: "Home", path: "/", anchorId: "", icon: "ri-home-4-line" },
  { label: "About", path: "/about", anchorId: "about", icon: "ri-user-heart-line" },
  { label: "Session", path: "/session-intro", anchorId: "", icon: "ri-heart-line" },
  { label: "Menu", path: "/session", anchorId: "", icon: "ri-camera-line" },
  { label: "Price", path: "/price", anchorId: "price", icon: "ri-price-tag-3-line" },
  { label: "Schedule", path: "/schedule", anchorId: "schedule", icon: "ri-calendar-2-line" },
  { label: "How to Book", path: "/how-to-book", anchorId: "how-to-book", icon: "ri-book-open-line" },
  { label: "Gallery", path: "/gallery", anchorId: "gallery", icon: "ri-image-2-line" },
  { label: "Video", path: "/video", anchorId: "video", icon: "ri-film-line" },
  { label: "Reviews", path: "/reviews", anchorId: "reviews", icon: "ri-star-line" },
  { label: "Links & SNS", path: "/links-sns", anchorId: "blog", icon: "ri-links-line" },
  { label: "Reservation", path: "/reservation", anchorId: "contact", icon: "ri-calendar-check-line" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeAnchor, setActiveAnchor] = useState("");
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 1024);
  const navigate = useNavigate();
  const location = useLocation();

  const isHome = location.pathname === "/";
  const isMobile = () => window.innerWidth < 768;

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
      if (location.pathname === "/") {
        const anchors = navItems.map((i) => i.anchorId).filter(Boolean);
        let current = "";
        for (const id of anchors) {
          const el = document.getElementById(id);
          if (el) {
            const rect = el.getBoundingClientRect();
            if (rect.top <= 100) current = id;
          }
        }
        setActiveAnchor(current);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname]);

  useEffect(() => {
    setMenuOpen(false);
    setActiveAnchor("");
    window.scrollTo(0, 0);
    // ページ遷移時に必ずスクロールロックを解除
    document.body.style.overflow = "";
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const handleNav = (path: string, anchorId: string) => {
    setMenuOpen(false);

    // スマホ版で Links & SNS → Homeページの #blog にスクロール（独立ページへは遷移しない）
    if (isMobile() && path === "/links-sns") {
      if (location.pathname === "/") {
        const el = document.getElementById("blog");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate("/");
        setTimeout(() => {
          const el = document.getElementById("blog");
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }, 300);
      }
      return;
    }

    if (isMobile() && path === "/" && !anchorId) {
      if (location.pathname === "/") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        navigate("/");
        setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
      }
      return;
    }
    if (isMobile() && anchorId && location.pathname === "/") {
      const el = document.getElementById(anchorId);
      if (el) { el.scrollIntoView({ behavior: "smooth" }); return; }
    }
    if (isMobile() && anchorId && location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(anchorId);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 300);
      return;
    }
    navigate(path);
  };

  const isTransparent = false;

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 transition-all duration-500"
        style={{
          zIndex: 9990,
          backgroundColor: isTransparent ? "transparent" : "rgba(255,255,255,0.97)",
          borderBottom: isTransparent ? "1px solid transparent" : "1px solid rgba(231,229,228,0.8)",
          backdropFilter: isTransparent ? "none" : "blur(12px)",
          WebkitBackdropFilter: isTransparent ? "none" : "blur(12px)",
        }}
      >
        <div className="px-4 md:px-10 h-14 md:h-16 flex items-center justify-between gap-4">
          {/* ロゴ */}
          <button
            onClick={() => handleNav("/", "")}
            className="cursor-pointer bg-transparent border-none p-0 flex items-center flex-shrink-0"
          >
            <img
              src="https://storage.readdy-site.link/project_files/5fc4a5c0-7993-4a64-a378-6d67b6cf5ae0/0e574521-e1b7-4e55-a413-3e988014921a_-2.png?v=6afdbb476c9a331f0ce14e8b736cad04"
              alt="Mugi Cha Production"
              className="h-6 md:h-8 w-auto object-contain"
            />
          </button>

          {/* デスクトップナビ */}
          <nav className="hidden lg:flex items-center flex-wrap justify-center gap-y-1 flex-1">
            {navItems.map((item, index) => {
              const isActive = isHome
                ? (item.path === "/blog"
                    ? false
                    : !item.anchorId ? item.path === "/" && activeAnchor === "" : activeAnchor === item.anchorId)
                : location.pathname === item.path;
              return (
                <div key={item.path} className="flex items-center">
                  {index !== 0 && (
                    <span
                      className={`w-px h-3 mx-3 flex-shrink-0 transition-colors duration-500 ${
                        isTransparent ? "bg-stone-400/40" : "bg-stone-300/60"
                      }`}
                    ></span>
                  )}
                  <button
                    onClick={() => handleNav(item.path, item.anchorId)}
                    className={`relative text-base tracking-[0.12em] transition-colors duration-300 cursor-pointer whitespace-nowrap bg-transparent border-none p-0 group ${
                      isActive
                        ? "text-stone-900 font-medium"
                        : isTransparent
                        ? "text-stone-600 hover:text-stone-900"
                        : "text-stone-500 hover:text-stone-800"
                    }`}
                  >
                    {item.label}
                    <span
                      className={`absolute -bottom-0.5 left-0 h-px bg-amber-500 transition-all duration-300 ${
                        isActive ? "w-full" : "w-0 group-hover:w-full"
                      }`}
                    ></span>
                  </button>
                </div>
              );
            })}
          </nav>

          {/* ハンバーガーボタン（スマホ） */}
          <button
            className="lg:hidden w-10 h-10 flex flex-col items-center justify-center gap-[5px] cursor-pointer flex-shrink-0"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="メニュー"
          >
            <span className={`block w-5 h-[1.5px] bg-stone-700 transition-all duration-300 origin-center ${menuOpen ? "rotate-45 translate-y-[6.5px]" : ""}`}></span>
            <span className={`block w-5 h-[1.5px] bg-stone-700 transition-all duration-300 ${menuOpen ? "opacity-0 scale-x-0" : ""}`}></span>
            <span className={`block w-5 h-[1.5px] bg-stone-700 transition-all duration-300 origin-center ${menuOpen ? "-rotate-45 -translate-y-[6.5px]" : ""}`}></span>
          </button>
        </div>
      </header>

      {/* オーバーレイ（header外・独立したfixed要素） */}
      {menuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 backdrop-blur-sm"
          style={{ zIndex: 9998 }}
          onClick={() => setMenuOpen(false)}
        />
      )}

      {/* ドロワー（header外・独立したfixed要素） */}
      <div
        className={`lg:hidden fixed top-0 right-0 h-full w-[80vw] max-w-xs transition-transform duration-350 ease-in-out ${
          menuOpen ? "translate-x-0" : "translate-x-full"
        } bg-white flex flex-col`}
        style={{ zIndex: 9999, boxShadow: menuOpen ? "-4px 0 24px rgba(0,0,0,0.10)" : "none" }}
      >
        {/* ドロワーヘッダー */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-stone-100 flex-shrink-0">
          <button
            onClick={() => handleNav("/", "")}
            className="cursor-pointer bg-transparent border-none p-0 flex items-center"
          >
            <img
              src="https://storage.readdy-site.link/project_files/5fc4a5c0-7993-4a64-a378-6d67b6cf5ae0/0e574521-e1b7-4e55-a413-3e988014921a_-2.png?v=6afdbb476c9a331f0ce14e8b736cad04"
              alt="Mugi Cha Production"
              className="h-7 w-auto object-contain"
            />
          </button>
          <button
            onClick={() => setMenuOpen(false)}
            className="w-10 h-10 flex items-center justify-center text-stone-400 hover:text-stone-700 active:bg-stone-100 rounded-full transition-colors cursor-pointer"
            aria-label="閉じる"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* ナビアイテム */}
        <nav className="flex-1 overflow-y-auto py-3">
          {navItems.map((item) => {
            const isActive = (() => {
              if (isMobile() && location.pathname === "/") {
                if (item.path === "/links-sns") return activeAnchor === "blog";
                if (!item.anchorId) return item.path === "/" && activeAnchor === "";
                return activeAnchor === item.anchorId;
              }
              return location.pathname === item.path;
            })();
            return (
              <button
                key={item.path}
                onClick={() => handleNav(item.path, item.anchorId)}
                className={`relative w-full flex items-center gap-3.5 px-5 py-4 cursor-pointer transition-colors duration-150 bg-transparent border-none text-left active:bg-stone-50 ${
                  isActive
                    ? "bg-amber-50/60 text-stone-900"
                    : "text-stone-600 hover:bg-stone-50 hover:text-stone-900"
                }`}
              >
                {/* アクティブインジケーター */}
                <div className={`absolute left-0 w-0.5 h-8 rounded-r-full transition-all duration-200 ${isActive ? "bg-amber-500" : "bg-transparent"}`}></div>

                <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 transition-colors duration-150 ${
                  isActive ? "bg-stone-800 text-white" : "bg-stone-100 text-stone-500"
                }`}>
                  <i className={`${item.icon} text-sm`}></i>
                </div>
                <span className={`flex-1 text-sm tracking-[0.1em] ${isActive ? "font-medium text-stone-900" : "text-stone-600"}`}>
                  {item.label}
                </span>
                <i className={`ri-arrow-right-s-line text-base transition-colors duration-150 ${isActive ? "text-amber-500" : "text-stone-300"}`}></i>
              </button>
            );
          })}
        </nav>

        {/* ドロワーフッター：予約ボタン */}
        <div className="px-5 py-5 border-t border-stone-100 flex-shrink-0 space-y-3">
          <button
            onClick={() => handleNav("/reservation", "contact")}
            className="w-full py-3.5 bg-stone-800 text-white text-sm tracking-[0.18em] flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap active:bg-stone-700 transition-colors duration-150"
          >
            <i className="ri-calendar-check-line text-sm"></i>
            ご予約はこちら
          </button>
          <p className="text-[10px] text-stone-400 tracking-widest text-center">
            © 2024 Mugi Cha Production
          </p>
        </div>
      </div>
    </>
  );
}